import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'

import {
  authUser,
  resetPassword,
  resetPasswordConfirm,
  getResetPasswordData,
  emailConfirm,
  getEmailConfirmationData,
  sendFeedback,
  getAnonMarket,
  createUser,
  createMarket,
  createSoloMarket,
  bookingRequest,
  getShelfTypeServices,
  getShelfTypes,
  getShelfPeriodPrices,
  getShelfTypeAvailability,
  getBookingRequest,
  bookingPaymentTransaction,
  customerInitPayment,
  customerConfirmPayment,
  customerBookWithoutPayment,
  customerBookingPaymentTransaction,
} from '@anonymous/services'

import {
  baseActions,
  baseGetters,
  baseMutations,
  baseState,
  setToken,
  anonRequestInterceptor,
  customerRequestInterceptor,
  customerResponseFulfilledInterceptor,
  customerResponseRejectedInterceptor
} from '@base/store'

import { actionTypes, mutationTypes } from '@base/store/store-types'

Vue.use(Vuex)

const axiosInstances = [
  authUser,
  resetPassword,
  resetPasswordConfirm,
  getResetPasswordData,
  emailConfirm,
  getEmailConfirmationData,
  sendFeedback,
  getAnonMarket,
  createUser,
  createMarket,
  createSoloMarket,
  bookingRequest,
  getShelfTypes,
  getShelfTypeServices,
  getShelfPeriodPrices,
  getShelfTypeAvailability,
  getBookingRequest,
  bookingPaymentTransaction,
]

for (const instance of axiosInstances) {
  instance.interceptors.request.use(anonRequestInterceptor)
}

// Customer token + CSRF for an existing customer authenticating inside the embed iframe.
const customerAxiosInstances = [
  customerInitPayment,
  customerConfirmPayment,
  customerBookWithoutPayment,
  customerBookingPaymentTransaction,
]

for (const instance of customerAxiosInstances) {
  instance.interceptors.request.use(customerRequestInterceptor)
  instance.interceptors.response.use(
    customerResponseFulfilledInterceptor,
    customerResponseRejectedInterceptor
  )
}

const initialState = {
  ...baseState,
  resetPasswordData: {
    validLink: null,
    uid: null,
    token: null,
    user: null,
    email: null,
  },
  emailConfirmationData: {
    validLink: null,
    emailVerified: null,
    user: null,
    email: null,
    uid: null,
    token: null
  },
  shelfTypes: [],
  shelfPeriodPrices: [],
  shelfTypeServices: [],
  shelfTypeAvailability: null,
  selectedBookingRequest: {},
  marketId: null,
  anonMarket: null,
}

document.initialState = initialState      // Need in each section stores for base logout user behavior

export default new Vuex.Store({
  state: initialState,
  getters: {
    ...baseGetters,

  },
  mutations: {
    ...baseMutations,
    [mutationTypes.SET_RESET_PASSWORD_DATA](state, data) {
      state.resetPasswordData = data
    },
    [mutationTypes.SET_CONFIRMATION_DATA](state, data) {
      state.emailConfirmationData = data
    },
    [mutationTypes.SET_ANON_MARKET](state, market) {
      state.anonMarket = market
      state.marketId = market.marketId
    },
    [mutationTypes.SET_SHELF_TYPE_SERVICES_ANON](state, services) {
      state.shelfTypeServices = services
    },
    [mutationTypes.SET_SHELF_TYPES_ANON](state, types) {
      state.shelfTypes = types
    },
    [mutationTypes.SET_SHELF_PERIOD_PRICES_ANON](state, prices) {
      state.shelfPeriodPrices = prices
    },
    [mutationTypes.SET_SHELF_TYPE_AVAILABILITY_ANON](state, data) {
      state.shelfTypeAvailability = data
    },
    [mutationTypes.SET_BOOKING_REQUEST_ANON](state, obj) {
      state.selectedBookingRequest = obj
    },
  },
  actions: {
    ...baseActions,


    // eslint-disable-next-line no-unused-vars
    [actionTypes.LOGIN_USER]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        authUser({
          data: payloads
        })
          .then(response => {
            response.data.date = new Date()
            const camelCaseToken = _.mapKeys(response.data, (v, k) => _.camelCase(k))
            setToken(camelCaseToken)
            resolve(camelCaseToken)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.RESET_PASSWORD]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        resetPassword({
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.CONFIRM_RESET_PASSWORD]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        resetPasswordConfirm({
          url: `${payloads.uid}/${payloads.token}/`,
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_RESET_PASSWORD_DATA]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        getResetPasswordData({
          data: payloads
        })
          .then(response => {
            commit(mutationTypes.SET_RESET_PASSWORD_DATA, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_CONFIRMATION_DATA]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        getEmailConfirmationData({
          data: payloads
        })
          .then(response => {
            commit(mutationTypes.SET_CONFIRMATION_DATA, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.CONFIRM_EMAIL]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        emailConfirm({
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.SEND_FEEDBACK]({ commit }, payloads) {
      return new Promise((resolve, reject) => {
        sendFeedback({
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_ANON_MARKET]({ commit }, marketId) {
      return new Promise((resolve, reject) => {
        getAnonMarket({
          url: `${marketId}/`
        })
          .then(response => {
            if (response.data.marketId) {
              commit(mutationTypes.SET_ANON_MARKET, response.data)
              resolve(response.data)
            }
            reject(response.data)
          })
          .catch((error) => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.CREATE_USER]({ dispatch }, payloads) {
      return new Promise((resolve, reject) => {
        createUser({
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.CREATE_MARKET]({ dispatch }, payloads) {
      return new Promise((resolve, reject) => {
        createMarket({
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    // eslint-disable-next-line no-unused-vars
    [actionTypes.CREATE_SOLO_MARKET]({ dispatch }, payloads) {
      return new Promise((resolve, reject) => {
        createSoloMarket({
          data: payloads
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },

    [actionTypes.GET_SHELF_TYPE_SERVICES_ANON]({ commit, state }, shelfTypeId) {
      return new Promise((resolve, reject) => {
        getShelfTypeServices({
          url: `${state.marketId}/${shelfTypeId}/`
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_TYPE_SERVICES_ANON, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_TYPES_ANON]({ state, commit }) {
      return new Promise((resolve, reject) => {
        getShelfTypes({
          url: `${state.marketId}/`
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_TYPES_ANON, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_PERIOD_PRICES_ANON]({ state, commit }, shelfTypeId) {
      return new Promise((resolve, reject) => {
        getShelfPeriodPrices({
          url: `${state.marketId}/${shelfTypeId}/`
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_PERIOD_PRICES_ANON, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_SHELF_TYPE_AVAILABILITY_ANON]({ commit, state }, payload) {
      return new Promise((resolve, reject) => {
        getShelfTypeAvailability({
          url: `${state.marketId}/`,
          params: payload,
        })
          .then(response => {
            commit(mutationTypes.SET_SHELF_TYPE_AVAILABILITY_ANON, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_RESERVE_ANON]({ state, commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequest({
          url: `${state.marketId}/reserve/`,
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_INIT_PAYMENT_ANON]({ state, commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequest({
          url: `${state.marketId}/init_payment/`,
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_CONFIRM_PAYMENT_ANON]({ state, commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequest({
          url: `${state.marketId}/confirm_payment/`,
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_BOOK_WITHOUT_PAYMENT_ANON]({ state, commit }, payload) {
      return new Promise((resolve, reject) => {
        bookingRequest({
          url: `${state.marketId}/book_without_payment/`,
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_DELETE_ANON]({ state, commit }, { id, cartToken }) {
      return new Promise((resolve, reject) => {
        bookingRequest({
          method: "delete",
          url: `${state.marketId}/delete/${id}/`,
          params: { cart_token: cartToken },
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_PAYMENT_TRANSACTION_FAIL_ANON]({ state, commit }, bookingPaymentTransactionId) {
      return new Promise((resolve, reject) => {
        bookingPaymentTransaction({
          method: "delete",
          url: `${state.marketId}/fail/${bookingPaymentTransactionId}/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_PAYMENT_TRANSACTION_CANCEL_ANON]({ state, commit }, bookingPaymentTransactionId) {
      return new Promise((resolve, reject) => {
        bookingPaymentTransaction({
          method: "delete",
          url: `${state.marketId}/cancel/${bookingPaymentTransactionId}/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_INIT_PAYMENT]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        customerInitPayment({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_CONFIRM_PAYMENT]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        customerConfirmPayment({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_REQUEST_BOOK_WITHOUT_PAYMENT]({ commit }, payload) {
      return new Promise((resolve, reject) => {
        customerBookWithoutPayment({
          data: payload,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_PAYMENT_TRANSACTION_FAIL]({ commit }, bookingPaymentTxId) {
      return new Promise((resolve, reject) => {
        customerBookingPaymentTransaction({
          url: `${bookingPaymentTxId}/fail/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    // eslint-disable-next-line no-unused-vars
    [actionTypes.BOOKING_PAYMENT_TRANSACTION_CANCEL]({ commit }, bookingPaymentTxId) {
      return new Promise((resolve, reject) => {
        customerBookingPaymentTransaction({
          url: `${bookingPaymentTxId}/cancel/`,
        })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
    [actionTypes.GET_BOOKING_REQUEST_ANON]({ commit }, { bookingRequestId, queryPayload }) {
      return new Promise((resolve, reject) => {
        const query = new URLSearchParams(queryPayload).toString()
        getBookingRequest({
          url: `${bookingRequestId}/?${query}`,
        })
          .then(response => {
            commit(mutationTypes.SET_BOOKING_REQUEST_ANON, response.data)
            resolve(response.data)
          })
          .catch(error => {
            reject(error.response)
          })
      })
    },
  }
})
